import { CheckCircle2, Download, ShieldAlert } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useParams } from 'react-router';
import { downloadLinkedInstaller, isStatus, NetworkError } from '../lib/api';
import { AuthFrame } from '../session/SignedOut';
import { Button } from '../ui/Button';
import { cn } from '../ui/cn';
import { Logo } from '../ui/misc';

/**
 * Where an installer download link opens (WRMM-81), for the person at the computer to set up, without signing in. The
 * link is /install/:id#token: the token stays in the fragment, which browsers never send, and goes only in the body of
 * the download request.
 */
export function InstallPage() {
  const { id = '' } = useParams();
  const token = typeof window === 'undefined' ? '' : window.location.hash.replace(/^#/, '');
  const valid = /^[0-9a-f-]{36}$/.test(id) && /^[A-Za-z0-9_-]{43}$/.test(token);
  const [state, setState] = useState<{ phase: 'ready' | 'busy' } | { phase: 'done'; fileName: string } | { phase: 'failed'; message: string }>({ phase: 'ready' });

  const download = async () => {
    setState({ phase: 'busy' });
    try {
      const { file, fileName } = await downloadLinkedInstaller(id, token, new URLSearchParams(window.location.search).get('platform') === 'windows-arm64' ? 'windows-arm64' : 'windows-x64');
      const url = URL.createObjectURL(file);
      const link = Object.assign(document.createElement('a'), { href: url, download: fileName });
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setState({ phase: 'done', fileName });
    } catch (error) {
      setState({ phase: 'failed', message: isStatus(error, 404) || isStatus(error, 403) || isStatus(error, 400)
        ? 'This link has expired, has been used for as many computers as it allows, or was turned off. Ask WITTCO for a new one.'
        : isStatus(error, 429) ? 'Too many tries from this network. Wait a minute, then try again.'
        : isStatus(error, 409) ? 'The installer isn’t available right now. Let WITTCO know.'
        : error instanceof NetworkError ? 'The download didn’t start: check this computer’s internet connection and try again.'
        : 'The download didn’t work. Try again, or let WITTCO know.' });
    }
  };

  return (
    <AuthFrame>
      <div className="rounded-2xl border border-line bg-panel/95 p-8 shadow-pop backdrop-blur">
        <Logo className="size-10" />
        <h1 className="mt-6 text-[22px] font-semibold tracking-tight">Set up WITTCO support</h1>
        <p className="mt-1.5 text-ink-2">This installs WITTCO’s support agent on this Windows computer, so WITTCO can look after it and help you remotely.</p>
        {!valid ? (
          <Note tone="warn">This link is incomplete. Open the whole link from your message, or ask WITTCO for a new one.</Note>
        ) : state.phase === 'done' ? (<>
          <Note tone="ok">Downloaded <span className="font-mono">{state.fileName}</span>.</Note>
          <ol className="mt-4 list-decimal space-y-1.5 pl-5 text-[13.5px] text-ink-2">
            <li>Open it from your Downloads. Don’t rename it: its name tells it which business it’s for.</li>
            <li>If Windows says <span className="font-medium text-ink">Windows protected your PC</span>, choose <span className="font-medium text-ink">More info</span>, check the publisher is <span className="font-medium text-ink">WITTCO LLC</span>, then choose <span className="font-medium text-ink">Run anyway</span>. Windows shows this for programs it hasn’t seen often yet.</li>
            <li>When Windows asks whether to let it make changes, check that it says <span className="font-medium text-ink">WITTCO LLC</span>, then choose Yes. You need to be an administrator on this computer.</li>
            <li>It tells you when it’s done. Nothing else is needed.</li>
          </ol>
          <Button variant="ghost" className="mt-4" onClick={() => void download()}>Download again</Button>
        </>) : (<>
          {state.phase === 'failed' && <Note tone="warn">{state.message}</Note>}
          <Button variant="brand" className="mt-6 h-11 w-full justify-center text-[15px]" disabled={state.phase === 'busy'} onClick={() => void download()}>
            <Download className="size-4" />{state.phase === 'busy' ? 'Downloading…' : 'Download the installer'}</Button>
          <p className="mt-4 text-center text-[12.5px] text-ink-3">Open this link on the Windows computer you want to set up.</p>
        </>)}
      </div>
    </AuthFrame>
  );
}

function Note({ tone, children }: { tone: 'ok' | 'warn'; children: ReactNode }) {
  const Icon = tone === 'ok' ? CheckCircle2 : ShieldAlert;
  return (
    <p role="status" className={cn('mt-5 flex items-start gap-2 rounded-lg px-3 py-2.5 text-[13px]', tone === 'warn' ? 'bg-attention-soft text-attention' : 'bg-healthy-soft text-healthy')}>
      <Icon className="mt-px size-4 shrink-0" /><span>{children}</span>
    </p>
  );
}
